<template>
  <div
    class="line-item is-freebie"
    :class="{
      [$class('loading')]: !lineItem,
      [$class('updating')]: state === 'updating',
    }"
  >
    <p
      v-if="isFirstFreebie"
      class="line-item__freebie-label text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      v-text="giftTitle"
    />
    <div class="line-item__grid">
      <a class="line-item__link" :href="lineItem.url" tabindex="-1">
        <span class="visually-hidden" v-text="lineItem.product_title" />

        <div class="line-item__image-container">
          <responsive-image
            class="line-item__image"
            :image="lineItem.localized_image ?? lineItem.featured_image"
            :lazy-load="false"
            ratio="1:1"
            sizes="(min-width: 1024px) 107px, 101px"
            widths="107, 214, 321"
          />
        </div>
      </a>

      <div class="line-item__body">
        <div class="line-item__details">
          <a
            class="line-item__link line-item__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            :href="lineItem.url"
            :aria-label="lineItem.product_title"
            v-text="lineItem.product_title"
          />

          <p
            v-if="!lineItem.product_has_only_default_variant"
            class="line-item__variant line-item__truncate text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
          >
            <span v-text="variantText" />
          </p>
          <div class="line-item__price-container">
            <p
              class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold is-free"
              v-text="$string('product.price.free')"
            />
            <button
              class="line-item__remove"
              :class="{ [$class('disabled')]: state === 'updating' }"
              type="button"
              @click="handleRemove"
            >
              <span
                class="visually-hidden"
                v-text="$string('cart.line_item.remove')"
              />
              <icon-bin />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 *
 */
import { mapActions, mapState } from 'pinia';

import ResponsiveImage from '~global/images/responsive-image';

import IconBin from '~icons/general/bin.svg';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';

export default {
  name: 'LineItemFreebie',

  components: {
    IconBin,
    ResponsiveImage,
  },

  props: {
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      error: '',
      quantity: 1,
      state: 'loading',
      graduatedArray: [],
    };
  },

  computed: {
    ...mapState(useCartStore, {
      cartStatus: 'status',
      cartItems: (store) => store.response.items,
    }),

    giftTitle() {
      const freebieProperty = this.$variable('discount.freebies.property');
      const freebies = (this.cartItems || []).filter(
        (item) => item.properties?.[freebieProperty],
      );

      return this.$string('cart.freebies.added_title', {
        pluralise: freebies.length,
      });
    },

    /**
     * Compute variant text.
     * @returns {String}
     */
    variantText() {
      return this.lineItem.options_with_values
        ? this.lineItem.options_with_values
            .slice()
            .reverse()
            .map((option) => {
              return this.$string('cart.line_item.variant', {
                replace: { selected_value: option.value },
              });
            })
            .join(', ')
        : this.lineItem.variant_title;
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      );
    },

    /**
     * Zeigt das Label nur für das erste Freebie (erstes Item mit Freebie-Property) an.
     */
    isFirstFreebie() {
      if (!this.lineItem) return false;
      const freebieProperty = this.$variable('discount.freebies.property');
      const freebies = (this.cartItems || []).filter(
        (item) => item.properties?.[freebieProperty],
      );
      if (!freebies.length) return false;
      return freebies[0].key === this.lineItem.key;
    },
  },

  watch: {
    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating';
        return;
      }

      this.state = 'ready';
    },
  },

  mounted() {
    this.state = 'ready';
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useCartStore, {
      removeItem: 'updateItem',
    }),

    ...mapActions(useDiscountCampaignsStore, {
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),

    /**
     * Handle remove click.
     */
    async handleRemove() {
      if (this.state === 'updating') {
        return;
      }

      await this.removeItem(this.lineItem.key);
      this.setErrorType('');
      this.setCodeState('ready');
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription;
        return;
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`;
        })
        .join('<br>');
    },
  },
};
</script>

<style lang="scss">
@import './line-item';
</style>
